import crews from "csi/access/crews";
import JsonResponse from "csi/comm/JsonResponse";
import { C8Node, cnl, cnlPath, ICache, PathNodesStepCallback, PathNodeStepCallback } from "csi/typings";
import xml from "csi/xml";
import { ExtendedPathStep, parsePathSteps } from "csi/core/extendedPathService";
import { compact } from "lodash";
import { action, makeObservable, observable } from "mobx";

// TODO: xml.nodeApplyRollups?

export class Cache implements ICache {
	/** map from cnl to boolean indicating if node has been deleted (not just cached, but actually deleted on server) */
	public static deleted = new Set<cnl>();

	/** map from cnl to node */
	private hash = observable.map<cnl, C8Node>(null, { deep: false });

	/** map from cnl to timestamp of time when cached */
	private timestamps = observable.map<cnl, number>();

	/**
	 * Return array of all not deleted nodes in the cache
	 */
	get data(): C8Node[] {
		const nodes: C8Node[] = [];

		for (const [url, node] of this.hash) {
			if (!Cache.deleted.has(node.$URL)) {
				nodes.push(node);
			}
		}

		return nodes;
	}

	/**
	 * Return node with given url from cache if cached after given timestamp (if any)
	 */
	get(url: cnl, timestamp?: number): C8Node | undefined {
		// if node was deleted from any cache, then return undefined
		if (Cache.deleted.has(url)) {
			return;
		}

		const { hash, timestamps } = this;
		const node = hash.get(url);

		if (node && timestamp && (timestamps.get(url) < timestamp)) {
			return;
		}

		return node;
	}

	/**
	 * Get array of nodes from given cnls
	 */
	getNodes(urls: cnl[], timestamp?: number): (C8Node | undefined)[] {
		return urls?.map((url) => this.get(url, timestamp)) ?? [];
	}

	/**
	 * Find first node in cache that references the given url within the given attribute by a node of the given type
	 */
	getParent(targetUrl: cnl, attrId: string, nodeType?: string): C8Node | undefined {
		for (const [url, node] of this.hash) {
			if (!nodeType || (node.$Type === nodeType)) {
				if (!Cache.deleted.has(url) && xml.nodeAttributeContainsValue(node, attrId, targetUrl)) {
					return node;
				}
			}
		}
	}

	/**
	 * Find nodes in cache that reference the given url within the given attribute by a node of the given type
	 */
	getParents(targetUrl: cnl, attrId: string, nodeType?: string): C8Node[] {
		const nodes = [];

		for (const [url, node] of this.hash) {
			if (!nodeType || (node.$Type === nodeType)) {
				if (!Cache.deleted.has(url) && xml.nodeAttributeContainsValue(node, attrId, targetUrl)) {
					nodes.push(node);
				}
			}
		}

		return nodes;
	}

	/**
	 * Traverse path from given node, calling callback at each valid step in path if given
	 */
	getPathNode(urlOrNode: cnl | C8Node, path = "", tm = 0, stepCallback?: PathNodeStepCallback): C8Node | undefined {
		let node = (typeof urlOrNode === "string") ? this.get(urlOrNode, tm) : urlOrNode;

		if (!node || !path) {
			return node || undefined;
		}

		const steps = parsePathSteps(path);
		let nodePrevious = node;
		let url: string;

		for (const step of steps) {
			const { a, c, isParent, key } = step;

			if (isParent) {
				node = this.getParent(node.$URL, a, c);
			} else {
				url = xml.nodeAttributeGetKeyValue(node, a, key);

				if (typeof url === "string") {
					node = this.get(url, tm);
				} else {
					// url could be array, in which case we should not continue traversing even if only one result
					node = undefined;
				}
			}

			stepCallback?.(node, nodePrevious, a, !isParent, key);

			if (!node) {
				return;
			}

			nodePrevious = node;
		}

		return nodePrevious;
	}

	/**
	 * Return nodes at end of path; if some nodes along path are not cached and stopIfUnresolved, then undefined is returned
	 */
	getPathNodes(
		urlOrNode: cnl | C8Node,
		path: string,
		tm = 0,
		stopIfUnresolved = false,
		stepCallback?: PathNodesStepCallback,
	): C8Node[] | undefined {
		let node: C8Node;

		if (typeof urlOrNode === "string") {
			node = this.get(urlOrNode, tm);

			if (!node) {
				return (stopIfUnresolved && xml.isValidURL(urlOrNode)) ? undefined : [];
			}
		} else if (urlOrNode) {
			node = urlOrNode;
		} else {
			return [];
		}

		if (!path) {
			return [node];
		}

		const steps = parsePathSteps(path);

		return this._getNodesForSteps(node, steps, tm, stopIfUnresolved, stepCallback);
	}

	/**
	 * Test given url-paths to see if cache contains them, and optionally check timestamp for each node against given timestamp
	 */
	getUnresolvedUrlPaths(urlPaths: cnlPath[], tm = 0): cnlPath[] {
		return urlPaths.filter(([url, path]) => this.getPathNodes(url, path, tm, true) === undefined);
	}

	/**
	 * Return node for give url-path pair
	 */
	getUrlPathNode(urlPath: cnlPath): C8Node {
		const [url, path] = urlPath;
		return this.getPathNode(url, path);
	}

	/**
	 * Add node to cache, and return same node
	 */
	@action add(node: C8Node, timestamp = new Date().getTime()): C8Node | undefined {
		if (node) {
			const { hash, timestamps } = this;
			const { $URL } = node;
			const oldNode = hash.get($URL);

			// only update timestamp if node is different; node contents should not change, so the node
			// can only be different if not the same object
			if (node !== oldNode) {
				// TODO: only update hash if node really changed - the hash is observable, so we don't want to trigger
				// a change unless there really was one. Need to be careful though if client is making changes to the node
				// and caching it, such as when inline editing and holding changes, or when evaluating expressions. We would
				// need to ensure in those situations that the $PT was updated too.
				// if (node.$PT !== oldNode.$PT) {
				// 		hash.set($URL, node);
				// }

				hash.set($URL, node);
				timestamps.set($URL, timestamp);
				Cache.deleted.delete($URL);
			}

			return node;
		}

		return undefined;
	}

	/**
	 * Cache the given node
	 * @param node
	 */
	cacheNode(node: C8Node): void {
		this.add(node);
	}

	/**
	 * Cache information from given response
	 * FIX: Should we bring crew information into cache - maybe???
	 */
	@action cacheResponse(response: JsonResponse): void {
		if (response?.isValid()) {
			const timestamp = new Date().getTime();

			response.getCrews()?.forEach(({ URL, name }) => crews.setRolesOfCrew(URL, name || []));
			response.getDeletedUrls()?.forEach((url) => this.deleteNode(url));
			response.getNodes()?.forEach((node) => this.add(node, timestamp));
		}
	}

	/**
	 * Delete node from cache (same as remove for this Cache, but different behavior on WorkingCache)
	 */
	@action deleteNode(url: cnl): void {
		Cache.deleted.add(url);
		this.remove(url);
	}

	/**
	 * Remove node from cache
	 */
	@action remove(url: cnl): void {
		const { hash, timestamps } = this;
		hash.delete(url);
		timestamps.delete(url);
	}

	/**
	 * Return array of nodes at end of path; return undefined if nodes are missing from cache
	 */
	_getNodesForSteps(
		nodeStart: C8Node,
		steps: ExtendedPathStep[],
		tm = 0,
		stopIfUnresolved = false,
		stepCallback?: PathNodesStepCallback,
	): C8Node[] | undefined {
		const { timestamps } = this;
		let nodesPrevious = [nodeStart];
		let nodes: C8Node[];
		let unresolved = false;

		steps.every((step) => {
			const { a, key, isParent } = step;

			nodes = [];

			unresolved = nodesPrevious.some((node) => {
				if (key != null) {
					const url = xml.nodeAttributeGetKeyValue(node, a, key);

					if (node = this.get(url)) {
						nodes.push(node);
					} else if (xml.isValidURL(url)) {
						return true;
					}

					return false;
				}

				// handle case where operand forks to multiple paths
				return a.split(";").some((id) => {
					let results: C8Node[];

					if (isParent) {
						results = this.getParents(node.$URL, id);
						nodes = nodes.concat(results);
					} else {
						const value = node[id];

						if (Array.isArray(value)) {
							results = this.getNodes(value);
						} else if (xml.isValidURL(value)) {
							results = [this.get(value)];
						}

						if (results?.some((node) => !node)) {
							if (stopIfUnresolved) {
								return true;
							}

							results = compact(results);
						}

						if (results) {
							nodes = nodes.concat(results);
						}
					}
				});
			});

			// if timestamp provided, make sure all nodes are up to date
			if (tm && !unresolved) {
				unresolved = nodes.some((nodeNext) => tm > timestamps.get(nodeNext.$URL));
			}

			if (unresolved) {
				return false;
			}

			if (stepCallback) {
				// if result returned from function, then assign to nodes - otherwise keep the same
				const filteredNodes = stepCallback(nodeStart, nodesPrevious, nodes, a, !isParent, key);
				if (filteredNodes) {
					nodes = filteredNodes;
				}
			}

			nodesPrevious = nodes;
			return nodes.length;
		});

		return unresolved ? undefined : nodesPrevious;
	}

	constructor() {
		makeObservable(this);
	}
}

/**
 * rootCache is persistent cache that is used to store global instances and instances necessary to support current user
 */
export const rootCache = new Cache();

export default Cache;